import { useCallback, useEffect, useState } from 'react';
/* PURPOSE:
This custom hook will track a scrollable area including window resizing on it.
It should be used to disable buttons which scroll to the top or bottom of a scrollable
div, depending on the position of the scroll bar.
*/
/* ARGS:
This custom hook takes two arguments, both of which are optional.
The first, child length, returns early in the checkScrollHeight
function if falsey- but is also used to as a useEffect dependency, so the checkScrollHeight function will run when a new item is
add to the scrollable list. Without this argument, the effect will run for every render.
Second param is optional and determines the direction the scrolling will start from on every render. By default
scrolling goes top to bottom and starts on the top.
*/
/* RETURNS:
This custom hook returns an array of booleans, length 2, which represent the scroll state data
and a callback to update the referenced scrolling element.
*/
export var VERTICAL = 'VERTICAL';
export var HORIZONTAL = 'HORIZONTAL';
export var PARTIAL_PIXEL = 1;
/*
This threshold helps to accurate situations there is some space between the most right item,
and the end of client container, so we won't need to open a new page for it, and can ignore this space.
*/
var THRESHOLD = 20;
function useScrollButtonState(childLength, shouldScrollToBottom, scrollOrientation, visibleListSize) {
    if (scrollOrientation === void 0) { scrollOrientation = VERTICAL; }
    var _a = useState(), scrollRef = _a[0], setScrollRef = _a[1];
    var _b = useState(false), navScrollUpEnabled = _b[0], setNavScrollUpEnabled = _b[1];
    var _c = useState(false), navScrollDownEnabled = _c[0], setNavScrollDownEnabled = _c[1];
    var _d = useState(false), navScrollLeftEnabled = _d[0], setNavScrollLeftEnabled = _d[1];
    var _e = useState(false), navScrollRightEnabled = _e[0], setNavScrollRightEnabled = _e[1];
    var _f = useState(0), currentPage = _f[0], setCurrentPage = _f[1];
    var _g = useState(0), lastPage = _g[0], setLastPage = _g[1];
    var scrollToBottom = useState(!!shouldScrollToBottom)[0];
    var scrollHorizontal = function (offset, scrollDirection, scrollRef) {
        if (scrollDirection > 0) {
            scrollRef.scrollLeft += offset;
        }
        else {
            scrollRef.scrollLeft -= offset;
        }
    };
    var scrollVertical = function (delta, scrollRef) {
        var newScrollTop = scrollRef.scrollTop + delta;
        if (newScrollTop < 0) {
            scrollRef.scrollTop = 0;
        }
        else if (scrollRef.offsetHeight + newScrollTop >= scrollRef.scrollHeight) {
            scrollRef.scrollTop = scrollRef.scrollHeight - scrollRef.clientHeight + PARTIAL_PIXEL;
        }
        else {
            scrollRef.scrollTop = newScrollTop;
        }
    };
    var onPageChanged = useCallback(function (value) {
        if (scrollRef) {
            var scrollDirection = value - currentPage;
            var children = scrollRef.children;
            if (scrollOrientation === HORIZONTAL) {
                scrollHorizontal(children[0].clientWidth, scrollDirection, scrollRef);
            }
            else if (scrollOrientation === VERTICAL) {
                scrollVertical((value - currentPage) * scrollRef.clientHeight, scrollRef);
            }
        }
    }, [currentPage, scrollRef]);
    var onClickNavButtonDown = useCallback(function () {
        onPageChanged(currentPage + 1);
    }, [onPageChanged, currentPage]);
    var onClickNavButtonUp = useCallback(function () {
        onPageChanged(currentPage - 1);
    }, [onPageChanged, currentPage]);
    var setRef = useCallback(function (instance) {
        if (instance) {
            setScrollRef(instance);
        }
    }, []);
    useEffect(function () {
        if (scrollToBottom) {
            if (childLength && lastPage > 1) {
                if (scrollRef) {
                    scrollRef.scrollTop = scrollRef.scrollHeight - scrollRef.clientHeight + PARTIAL_PIXEL;
                }
            }
        }
    }, [childLength, scrollRef, lastPage, visibleListSize]);
    useEffect(function () {
        if (childLength === undefined || childLength > 0) {
            if (scrollOrientation === HORIZONTAL) {
                checkScrollWidth(scrollRef, setNavScrollLeftEnabled, setNavScrollRightEnabled, setCurrentPage, setLastPage, childLength);
            }
            else {
                checkScrollHeight(scrollRef, setNavScrollUpEnabled, setNavScrollDownEnabled, setCurrentPage, setLastPage, childLength);
            }
        }
    });
    useEffect(function () {
        var onWindowResizeAndScroll = function () {
            if (scrollOrientation === HORIZONTAL) {
                checkScrollWidth(scrollRef, setNavScrollLeftEnabled, setNavScrollRightEnabled, setCurrentPage, setLastPage, childLength);
            }
            else {
                checkScrollHeight(scrollRef, setNavScrollUpEnabled, setNavScrollDownEnabled, setCurrentPage, setLastPage, childLength);
            }
        };
        window.addEventListener('resize', onWindowResizeAndScroll);
        if (scrollRef) {
            scrollRef.addEventListener('scroll', onWindowResizeAndScroll);
        }
        return function () {
            window.removeEventListener('resize', onWindowResizeAndScroll);
            if (scrollRef) {
                scrollRef.removeEventListener('scroll', onWindowResizeAndScroll);
            }
        };
    });
    return [
        {
            navScrollUpEnabled: navScrollUpEnabled,
            navScrollDownEnabled: navScrollDownEnabled,
            navScrollLeftEnabled: navScrollLeftEnabled,
            navScrollRightEnabled: navScrollRightEnabled,
            currentPage: currentPage,
            lastPage: lastPage,
            onPageChanged: onPageChanged,
            onClickNavButtonDown: onClickNavButtonDown,
            onClickNavButtonUp: onClickNavButtonUp,
        },
        setRef,
    ];
}
var checkScrollWidth = function (scrollParent, setNavScrollLeftEnabled, setNavScrollRightEnabled, setCurrentPage, setLastPage, childLength) {
    if (!scrollParent || childLength === 0) {
        setNavScrollRightEnabled(false);
        setNavScrollLeftEnabled(false);
        setCurrentPage(0);
        setLastPage(0);
        return;
    }
    var parentWidth = scrollParent.clientWidth;
    var parentOffsetWidth = scrollParent.offsetWidth + THRESHOLD;
    var parentScrollWidth = scrollParent.scrollWidth;
    var parentScrollLeft = scrollParent.scrollLeft;
    var childWidth = Array.from(scrollParent.children).reduce(function (total, childElement) {
        return total + childElement.clientWidth;
    }, 0);
    var isScrollable = childWidth > parentWidth;
    var middleScrolled = isScrolledToMiddle(parentOffsetWidth, parentScrollLeft, parentScrollWidth);
    var rightScrolled = isScrolledToRight(parentOffsetWidth, parentScrollLeft, parentScrollWidth);
    var leftScrolled = isScrolledToLeft(parentScrollLeft);
    var lastPage = calculateHorizontalModeLastPage(parentScrollWidth, parentWidth);
    var currentPage = rightScrolled
        ? lastPage
        : calculateHorizontalModeCurrentPage(parentScrollLeft, parentWidth);
    setNavScrollLeftEnabled(isScrollable && (middleScrolled || rightScrolled));
    setNavScrollRightEnabled(isScrollable && (middleScrolled || leftScrolled));
    setCurrentPage(isScrollable ? currentPage : 1);
    setLastPage(isScrollable ? lastPage : 1);
};
var checkScrollHeight = function (scrollParent, setNavScrollUpEnabled, setNavScrollDownEnabled, setCurrentPage, setLastPage, childLength) {
    if (!scrollParent || childLength === 0) {
        setNavScrollDownEnabled(false);
        setNavScrollUpEnabled(false);
        setCurrentPage(0);
        setLastPage(0);
        return;
    }
    var parentHeight = scrollParent.clientHeight;
    var parentOffsetHeight = scrollParent.offsetHeight;
    var parentScrollHeight = scrollParent.scrollHeight;
    var parentScrollTop = scrollParent.scrollTop;
    var childHeight = Array.from(scrollParent.children).reduce(function (total, childElement) {
        return total + childElement.clientHeight;
    }, 0);
    var isScrollable = childHeight > parentHeight;
    var middleScrolled = isScrolledToMiddle(parentOffsetHeight, parentScrollTop, parentScrollHeight);
    var bottomScrolled = isScrolledToBottom(parentOffsetHeight, parentScrollTop, parentScrollHeight);
    var topScrolled = isScrolledToTop(parentScrollTop);
    var lastPage = calculateLastPage(parentScrollHeight, parentHeight);
    var currentPage = bottomScrolled
        ? lastPage
        : calculateCurrentPage(parentScrollTop, parentHeight);
    setNavScrollUpEnabled(isScrollable && (middleScrolled || bottomScrolled));
    setNavScrollDownEnabled(isScrollable && (middleScrolled || topScrolled));
    setCurrentPage(isScrollable ? currentPage : 1);
    setLastPage(isScrollable ? lastPage : 1);
};
function isScrolledToBottom(offset, scrollTop, scrollHeight) {
    return offset + scrollTop >= scrollHeight;
}
function isScrolledToRight(offset, scrollLeft, scrollWidth) {
    return offset + scrollLeft >= scrollWidth;
}
function isScrolledToMiddle(offset, scrollTop, scrollHeight) {
    return offset + scrollTop < scrollHeight && scrollTop !== 0;
}
function isScrolledToLeft(scrollLeft) {
    return scrollLeft === 0;
}
function isScrolledToTop(scrollTop) {
    return scrollTop === 0;
}
function calculateCurrentPage(scrollTop, parentHeight) {
    return Math.ceil(scrollTop / parentHeight) + 1;
}
function calculateLastPage(parentHeight, scrollAreaHeight) {
    return Math.ceil(parentHeight / scrollAreaHeight);
}
function calculateHorizontalModeCurrentPage(scrollLeft, parentWidth) {
    if (scrollLeft === 0)
        return 1;
    return Math.ceil(scrollLeft / parentWidth);
}
function calculateHorizontalModeLastPage(parentScrollWidth, parentWidth) {
    if (parentScrollWidth === parentWidth)
        return 0;
    return Math.ceil(parentScrollWidth / parentWidth);
}
export default useScrollButtonState;
